'use client';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface Language {
  code: string;
  name: string;
  flag: string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', flag: '/images/languages/gb.svg' },
  { code: 'de', name: 'Deutsch', flag: '/images/languages/de.svg' },
  { code: 'es', name: 'Español', flag: '/images/languages/es-ct.svg' },
];

// { code: "fr", name: "Français", flag: "/images/languages/fr.svg" },
// { code: "it", name: "Italiano", flag: "/images/languages/it.svg" },

export function LanguageSelector() {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState<Language | null>(null);

  // Initialize language on component mount
  useEffect(() => {
    // Check for saved language first
    const savedLanguage = localStorage.getItem('language');

    if (savedLanguage) {
      const language = languages.find(lang => lang.code === savedLanguage);
      if (language) {
        setCurrentLanguage(language);
        i18n.changeLanguage(language.code);
        return;
      }
    }

    // Browser language detection
    const browserLang = navigator.language.split('-')[0];
    const detectedLanguage = languages.find(lang => lang.code === browserLang);

    if (detectedLanguage) {
      setCurrentLanguage(detectedLanguage);
      i18n.changeLanguage(detectedLanguage.code);
      localStorage.setItem('language', detectedLanguage.code);
    } else {
      // Default to German if browser language not found
      const defaultLanguage = languages.find(lang => lang.code === 'de');
      if (defaultLanguage) {
        setCurrentLanguage(defaultLanguage);
        i18n.changeLanguage(defaultLanguage.code);
        localStorage.setItem('language', defaultLanguage.code);
      }
    }
  }, [i18n]);

  const changeLanguage = (language: Language) => {
    // Ensure the language change applies globally
    setCurrentLanguage(language);
    i18n.changeLanguage(language.code);
    localStorage.setItem('language', language.code);

    // Force a reload of the page to ensure all components update their translations
    // This is optional but helps with components that might not react to i18n changes
    // window.location.reload();

    // Trigger a custom event that other components can listen for
    const event = new CustomEvent('languageChanged', { detail: language.code });
    window.dispatchEvent(event);
  };

  if (!currentLanguage) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger className="focus:outline-none">
        <div className="flex items-center cursor-pointer px-2 py-1 text-xs font-medium bg-transparent border border-slate-200 dark:border-slate-700 rounded hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
          {currentLanguage.code.toUpperCase()}
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[5rem]">
        {languages.map(language => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => changeLanguage(language)}
            className={`flex items-center justify-center cursor-pointer ${
              currentLanguage.code === language.code ? 'bg-slate-200 dark:bg-slate-700' : ''
            }`}
          >
            <span className="text-xs font-medium">{language.code.toUpperCase()}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
